import React, { useLayoutEffect, useRef } from 'react';
import cx from 'classnames';
import ReactDom from 'react-dom';
import Theme, { useThemeContext } from '../Theme';

import styles from './Portal.module.scss';
export interface Props {
  /** The element to the will appear inside of the portal container */
  children: React.ReactNode;
  /** Add a custom className. */
  className?: Parameters<typeof cx>[0];
  /** Specific the root element that portal node should be append to */
  mountedNode?: HTMLElement | null;
}

/**
 *
 * Portal is a util component that will allow elements to be rendered outside the DOM hierarchy of the parent component.
 * These are useful for popovers and modals.
 */
export const Portal: React.FC<Props> = ({
  children,
  className,
  mountedNode = document.body,
}) => {
  const portalNode = useRef<HTMLElement | null>(null);
  const theme = useThemeContext();

  if (portalNode.current === null) {
    portalNode.current = document.createElement('div');
    portalNode.current.className = cx(styles.portal, className);
  }

  // Since we're doing DOM manipulation, using useLayoutEffect so that changes are handled synchronously.
  // https://reactjs.org/docs/hooks-reference.html#uselayouteffect
  useLayoutEffect(() => {
    if (portalNode.current !== null) {
      mountedNode?.appendChild(portalNode.current);
    }

    // Removes the portal node when component gets unmounted.
    return () => {
      if (portalNode.current !== null) {
        portalNode.current && mountedNode?.removeChild(portalNode.current);
        portalNode.current = null;
      }
    };
  }, [portalNode, mountedNode]);

  return ReactDom.createPortal(
    <Theme theme={theme}>{children}</Theme>,
    portalNode.current,
  );
};

Portal.displayName = 'Portal';
export default Portal;
export { styles };
